import React from 'react'
import { Heading, Text, UnorderedList, ListItem, VStack } from '@chakra-ui/layout'
import { PageWrapper } from 'components/PageWrapper'
import { Container } from 'components/Container'

const Privacy = () => (
  <PageWrapper title="Terms and conditions">
    <Container>
      <VStack
        sx={{
          'h1,h2': { marginTop: '2rem !important' },
        }}
      >
        <Heading as="h1" size="2xl">
          Privacy policy
        </Heading>
        <Text>
          Hello! Thank you for visiting plantico.io. This page outlines the conditions under which
          you may access and use our website and platform, whether as a registered user or guest.
          Please carefully review these terms of use. By utilizing the site, you acknowledge and
          agree to comply with the terms of use. Failure to accept them means that you cannot use
          the site. PalmTech s.r.o, VAT n.: SK2120437220, registered in Slovakia, operates
          plantico.io (“We”, “Us”, “Our”, “Plantico”). If you need to contact us, please email
          hello@plantico.io or use the Help Portal on our site.
        </Text>
        <Heading size="2xl">Terms</Heading>
        <Heading>Description of Service</Heading>
        <Text>
          Plantico.io is a platform and not a financial institution, creditor, broker, advisor,
          charity, or merchant. It allows a Creator (as defined below) to register and personalize a
          page, such as www.plantico.io/PageName, with images, text, and video. Creators can use the
          page to accept monetary Donations (“Donations”), sell commissions (“Commissions”), offer
          products (the “Plantico Shop”), Memberships (“Memberships”), and other forms of Creator
          content, which will be collectively referred to as “Creator Content”. Supporters can
          follow, donate, purchase, or subscribe to receive Creator Content.
        </Text>
        <Heading>1. Scope</Heading>
        <Text>
          Plantico , plantico.io provided by PalmTech s.r.o. VAT n.: SK2120437220 registered in
          Slovakia.
          <br />
          As regards the Plantico websites, tools, and mobile applications, this Privacy Policy
          explains how we manage your personal information. It generally pertains to information
          collected on the plantico.io website, mobile application, and content (collectively
          referred to as the “Site” or “Sites”) or through the usage of our Service. The capitalized
          terms used in this Privacy Policy shall have the meaning as set forth herein or in the
          User Agreement posted on the Site. If you have established a Plantico account before the
          “last revised” date mentioned above, this Privacy Policy is effective as of August 15th,
          2022. For all other individuals, the Privacy Notice is effective immediately.
        </Text>
        <Heading>2. Types of Data We Collect</Heading>
        We may collect and store the following Personal Data:
        <Heading as="h3" size="md">
          The information you provide to us.
        </Heading>
        <UnorderedList>
          <ListItem>
            Contact information such as your name, user ID, email address, and phone number.
          </ListItem>
          <ListItem>
            We may also require information necessary to remit payments to sellers, such as
            financial account transactional information based on your activities on the Site as a
            seller (such as item and content you generate or that relates to your account).
          </ListItem>
          <ListItem>
            Additionally, we may collect information from community discussions, chats, dispute
            resolution, correspondence through our Site, and correspondence sent to us.
          </ListItem>
          <ListItem>
            We may also ask you to submit information to authenticate yourself or if we believe you
            are violating site policies (for example, we may ask you to send us an ID or bill to
            verify your address, or to answer additional questions online to help verify your
            identity or ownership of an item you list).
          </ListItem>
          <ListItem>
            Furthermore, we may collect marketing information such as your preferences for receiving
            marketing communications and details about how you engage with marketing communications.
          </ListItem>
          <ListItem>
            Finally, we may collect other personal information that you choose to submit to us.
          </ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          Information we gather about buyers either from the sellers or through them.
        </Heading>
        <UnorderedList>
          <ListItem>
            We may also collect information about buyers from or on behalf of our sellers.
          </ListItem>
          <ListItem>
            The scope of the information we collect on their behalf, that they transfer to us, or
            that we otherwise collect, is determined by the sellers, and the information we receive
            may vary by seller.
          </ListItem>
          <ListItem>
            Typically, the information we collect about individuals from or on behalf of our sellers
            includes transactional information based on your activities on the Site as a buyer (such
            as item and content you purchase), payment information such as credit card number, your
            contact information (such as your name, user ID, email-address, and shipping, billing
            and other information you provide to purchase an item or receive delivery of an item),
            and other personal information that our sellers may provide about you.
          </ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          Automatically Collected Information.
        </Heading>
        <UnorderedList>
          <ListItem>{`As soon as you visit the Site, we, along with our business partners and service providers, may automatically record details about you, your computer or mobile device, and your actions on our Site. This information may comprise your device's operating system, version number, manufacturer and model, browser type, screen resolution, IP address, general location data such as city, state or geographic area, and information about your use of our Site, including pages you viewed, the duration of your visit, navigation paths, details about your activity on a page, access times, and duration of access. Our service providers and business partners may gather this information over a period of time and across third-party websites. Cookies and similar technologies are used to collect this information.${''}`}</ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          Social Media Networks and other Third Party Platforms.
        </Heading>
        <UnorderedList>
          <ListItem>
            As a Service, Plantico may provide single sign-on options that allow you to utilize
            third-party login credentials. With your consent, Plantico may also gather profile
            information that is available in your third-party profile. Additionally, Plantico may
            allow you to import information about your connections and share information with
            third-party sites.
          </ListItem>
          <ListItem>
            {`Plantico may also maintain pages for our company and products on various third-party
            platforms, such as Facebook, Twitter, Google+, YouTube, Instagram, and other social
            networking services. When you interact with our pages on those third-party platforms,
            the relevant platform's privacy policy will govern your interactions. If the third-party
            platform provides us with information about our pages on those platforms or your
            interactions with them, we will handle that information in accordance with this Privacy
            Policy.${''}`}
          </ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          Sensitive Personal Information.
        </Heading>
        <UnorderedList>
          <ListItem>
            As a precautionary measure, we kindly request that you refrain from sending or revealing
            any sensitive personal information, such as social security numbers, details related to
            health, biometrics or genetic characteristics, criminal background or trade union
            membership, etc., via the Sites or any other means of communication with us. In the
            event that you do provide us with any sensitive personal information while utilizing our
            Services, you must provide your consent to our processing and utilization of such
            sensitive personal information, in accordance with our Privacy Policy. If you do not
            consent to our processing and use of such sensitive personal information, please refrain
            from submitting such information via our Sites.
          </ListItem>
        </UnorderedList>
        <Heading>3. Cookies and Similar Technologies</Heading>
        <Heading as="h3" size="md">
          What are cookies?
        </Heading>
        <Text>
          As a website user, you may come across cookies which are small files that are stored on
          your computer or mobile device by a website. Our Sites may use both session cookies, which
          expire after closing your web browser, and persistent cookies, which stay on your computer
          or mobile device until you delete them. The cookies are used to provide you with a more
          personalized and interactive experience on our Site. We use two categories of cookies,
          including first party cookies that are directly served by us to your computer or mobile
          device, and third party cookies that are served by service providers or business partners
          on our Sites. These cookies can be used by such service providers or business partners to
          recognize your computer or mobile device when it visits other websites.
        </Text>
        <Heading as="h3" size="md">
          Cookies we use
        </Heading>
        The following types of cookies are used by Our Site for the purposes set out below:
        <UnorderedList>
          <ListItem>
            <strong>Essential Cookies</strong> - These cookies are crucial as they enable you to
            access and use the services available through our Site. They are necessary to provide
            you with the services you have requested, and we solely use them for this purpose.
            Without these cookies, you would not be able to access the services that you need.
          </ListItem>

          <ListItem>
            <strong>Functionality Cookies</strong> - These cookies enable our Sites to retain your
            preferences and choices that you make while using our Sites. The aim of these cookies is
            to provide you with a personalized experience and to save you from the hassle of
            re-selecting your preferences every time you visit our Site.
          </ListItem>

          <ListItem>
            <strong>Analytics and Performance Cookies</strong> - These cookies collect data about
            the traffic on our Sites and how individuals use our Sites. The information collected
            may include the types of information described in the section titled{' '}
            {`"Information automatically collected."${''}`} We use this data to operate our Site more
            efficiently, gather demographic information, monitor the activity level on our Site, and
            improve the Site. To achieve this, we use Google Analytics, Optimizely, and New Relic.
            These parties use their own cookies. You can learn more about Google Analytics cookies
            at{' '}
            <a href="https://developers.google.com/analytics/resources/concepts/gaConceptsCookies">
              https://developers.google.com/analytics/resources/concepts/gaConceptsCookies
            </a>
            , and about how Google protects your data at{' '}
            <a href="https://www.google.com/analytics/learn/privacy.html">
              www.google.com/analytics/learn/privacy.html
            </a>
            . To prevent the use of Google Analytics related to your use of our Site, you can
            download and install the browser plugin available at{' '}
            <a href="https://tools.google.com/dlpage/gaoptout?hl=en-GB">
              https://tools.google.com/dlpage/gaoptout?hl=en-GB
            </a>
            .
          </ListItem>

          <ListItem>
            <strong>Social Media Cookies</strong> -{' '}
            {`When you use the social media sharing button or "like" button on our website or engage with our content on social networking websites such as Facebook or Twitter, we use cookies to track your activity. The social network will also use their own cookies to record your actions.${''}`}
          </ListItem>

          <ListItem>
            <strong>Targeted and advertising cookies</strong> - As you browse our website, certain
            cookies may be used to track your browsing habits. These cookies are used to deliver
            advertisements that are tailored to your interests by grouping you with other users who
            have similar browsing behavior. Advertisers can display relevant ads based on the
            cookies set by third-party advertising networks on our site and other sites.
          </ListItem>
        </UnorderedList>
        <Heading>4. Use of Your Personal Data</Heading>
        <Heading as="h3" size="md">
          To provide our Sites and Services.
        </Heading>
        <UnorderedList>
          <ListItem>Our network can assist you in establishing and securing your account.</ListItem>

          <ListItem>
            It can also identify you as a User within the system and send you a welcome e-mail to
            verify ownership of the e-mail address provided during account creation.
          </ListItem>

          <ListItem>
            We can address your inquiries related to employment opportunities or other requests,
            provide the Service and customer support you need, and resolve disputes, collect fees,
            and troubleshoot problems.{' '}
          </ListItem>

          <ListItem>
            Additionally, our system can help prevent, detect, and investigate potentially
            prohibited or illegal activities, and enforce our User Agreement.{' '}
          </ListItem>

          <ListItem>
            We use your communication preferences to inform you about our Service, service updates,
            and promotional offers.{' '}
          </ListItem>

          <ListItem>
            We compare information for accuracy and verify it with third parties.{' '}
          </ListItem>

          <ListItem>
            Lastly, we use the information for other purposes as described when we collect it.{' '}
          </ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          For contacting and communicating with you
        </Heading>
        As per your request for information from us (for instance, subscribing to our newsletter),
        registering on the Sites, or taking part in our contests or promotions, we may send you
        Plantico-associated marketing communications, subject to the laws. However, you will have
        the option to unsubscribe from such communications.
        <Heading as="h3" size="md">
          The utilization of personal data pertaining to buyers on behalf of our sellers.
        </Heading>
        The personal information we gather about buyers from or on behalf of our sellers is used
        solely as directed or authorized by the seller. We do not make use of this information for
        our own purposes. Our clients typically instruct or authorize us to utilize the personal
        information collected on their behalf to enable ecommerce and payments functionality on
        their websites, manage the delivery of digital goods, and facilitate communication between
        the sellers and their buyers.
        <Heading as="h3" size="md">
          To comply with law.
        </Heading>
        <UnorderedList>
          <ListItem>
            As deemed necessary or appropriate to adhere to applicable laws, lawful requests, and
            legal proceedings, such as responding to subpoenas or government authority requests, we
            utilize your personal information.
          </ListItem>
          <ListItem>
            Additionally, we use your personal information to enforce the terms and conditions that
            govern the Sites, protect our rights, privacy, safety or property, and/or that of you or
            others, and prevent fraudulent, harmful, unauthorized, unethical or illegal activity
            through protection, investigation and deterrence.
          </ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          To create anonymous data.
        </Heading>
        <UnorderedList>
          <ListItem>
            We can generate Anonymous Data from Personal Data by removing the information that can
            identify you personally. This Anonymous Data is then used to analyze the patterns of
            requests and usage, which helps us to improve the content of our Service and site
            navigation. We hold the right to use Anonymous Data for any purpose and share it with
            third parties at our discretion.
          </ListItem>
        </UnorderedList>
        <Heading>5. Our Disclosure of Your Personal Data</Heading>
        <Text>
          As per our policies, we may reveal Personal Data to comply with legal obligations, address
          claims of rights infringement related to a listing or other content, or safeguard the
          rights, property, or safety of any individual. The disclosure of such information will be
          in compliance with the relevant laws and regulations.
          <br />
          We may also share your Personal Data with:
        </Text>
        <UnorderedList>
          <ListItem>
            We engage service providers who are contracted to assist with our business operations,
            including but not limited to fraud investigations, bill collection, affiliate and
            rewards programs.
          </ListItem>
          <ListItem>
            We may also share your information with our subsidiaries and corporate affiliates
            consistent with our Privacy Policy.
          </ListItem>
          <ListItem>
            When you make a purchase through our Services, we may share your information with the
            seller.
          </ListItem>
          <ListItem>
            Additionally, we may disclose your information to third parties to whom you have
            explicitly requested us to send your information or when you have provided explicit
            consent to do so while using a specific service.
          </ListItem>
          <ListItem>
            We use a third-party payments processor, Stripe, to process payments made to us. Stripe
            may use personal information collected during payment processing as directed by us and
            for certain additional purposes, subject to its Privacy Policy.
          </ListItem>
          <ListItem>
            We may also share your information with professional advisers, such as lawyers, bankers,
            auditors, and insurers, as necessary in the course of their professional services to us.
          </ListItem>
          <ListItem>
            In response to verified requests relating to criminal investigations or illegal
            activities that may expose us, you, or any other Plantico User to legal liability, we
            may disclose relevant information to law enforcement, governmental agencies, or
            authorized third-parties. This information may include your name, city, state,
            ZIP/Postal code, telephone number, e-mail address, User ID history, IP address, fraud
            complaints, bidding and listing history, and anything else we may deem relevant to the
            investigation. In the event of a business transaction, such as a merger, consolidation,
            or acquisition, we may share your Personal Data with the other business entity involved.
          </ListItem>
          <ListItem>
            We will make reasonable efforts to ensure that the recipient follows our Privacy Policy
            with respect to your Personal Data.
          </ListItem>
          <ListItem>
            Finally, in the event of insolvency, bankruptcy, or receivership, your Personal Data may
            be transferred as a business asset.
          </ListItem>
        </UnorderedList>
        <Heading>6. Account Protection</Heading>
        <Text>
          You are responsible for protecting access to you account, either by securing your password
          or other types of authentications on Platico, e.g. your email.
        </Text>
        <Heading>7. Your Choices</Heading>
        <Heading as="h3" size="md">
          Accessing, Reviewing and Changing Your Personal Data
        </Heading>
        <Text>
          As a user, you have the ability to modify your Personal Data in your account by either
          editing your profile within your account or by sending an email to us at the email address
          provided below. If you choose to request the deletion of your Personal Data, please keep
          in mind that we may be obligated to retain this information and may be unable to delete
          it. However, we will comply with your deletion request as soon as reasonably possible,
          after fulfilling any legal requirements. If you wish to close your account, we will take
          action to remove your Personal Data from view based on your account activity and in
          compliance with applicable laws. It is important to note that we retain Personal Data from
          closed accounts in order to comply with legal obligations, prevent fraud, collect any
          outstanding fees, resolve disputes, investigate issues, enforce our User Agreement, and
          take other actions as permitted by law.
        </Text>
        <Heading>8. Security</Heading>
        <Text>
          As our servers are located in the United States, your information is stored on them. We
          employ various security technologies and procedures to safeguard your Personal Data from
          any unauthorized access, use, or disclosure. However, it is important to note that third
          parties may intercept or access transmissions or private communications unlawfully, and
          other Users may misuse or abuse your Personal Data collected from the Site. Therefore, we
          cannot guarantee that your Personal Data or private communications will always remain
          private, and it is advised not to expect such a guarantee.
        </Text>
        <Heading>9. Changes to this Privacy Policy</Heading>
        <Text>
          As the owners of this Privacy Policy, we have the right to change it at any time. We
          advise you to check this page regularly to stay informed about our privacy practices. If
          we make significant changes to this policy, we will update the date of the policy and post
          it on the Sites and in the app stores where our mobile applications are available for
          download. We may also notify you of any changes in another way that we believe will reach
          you, such as via email (if you have an account with us) or through the Sites. Any changes
          made to this Privacy Policy will be effective immediately upon posting the new terms on
          the Sites or upon implementation of the new changes. By continuing to use the Sites after
          the posting of any modified Privacy Policy, you are indicating your acceptance of the
          updated terms.
        </Text>
        <Heading>12. Contact Us</Heading>
        <Text>If you have any questions email us hello@plantico.io</Text>
        <Heading>13. Additional Information for European Union Users</Heading>
        <Heading as="h3" size="md">
          Personal information
        </Heading>
        <Text>{`References to “personal information” in this Privacy Policy are equivalent to “personal data” governed by European data protection legislation.${''}`}</Text>
        <Heading as="h3" size="md">
          Controller
        </Heading>
        <Text>
          As per the European data protection legislation, Plantico (PalmTech s.r.o.) is responsible
          for managing personal information collected for its own business purposes. The Contact Us
          section above contains the contact details. Plantico serves as a processor to sellers
          through the Services, as explained in this Privacy Policy. In situations where we act as a
          processor to a seller, the concerned seller becomes the data controller of your personal
          information processed in connection with the sale or delivery of goods to you.
        </Text>
        <Heading>Legal bases for processing</Heading>
        <Text>
          We utilize your personal information only in accordance with the law. We are obligated to
          notify you of the legal grounds for processing your personal information, which are
          outlined in the following table. If you have any queries regarding the legal basis for
          processing your personal information, kindly reach out to us at hello@plantico.io.
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>To provide our Sites and Services</strong> - We process your personal
            information because it is necessary to fulfill the contract that governs our provision
            of the Services, or to take steps that you request before signing up for the Services.
          </ListItem>
          <ListItem>
            <strong>
              To communicate with you, To create anonymous data, For compliance, fraud prevention
              and safety
            </strong>{' '}
            - The processing activities mentioned above are based on our legitimate interests. We
            ensure to consider and balance any potential impact on you, both positive and negative,
            and your rights before processing your personal information for our legitimate
            interests. We refrain from using your personal information for activities where our
            interests are outweighed by the impact on you, except where we have your consent or are
            legally required or permitted to do so.
          </ListItem>
          <ListItem>
            <strong>To comply with law</strong> - Processing is necessary to comply with our legal
            obligations
          </ListItem>
          <ListItem>
            <strong>With your consent</strong> - Processing is based on your consent. Where we rely
            on your consent you have the right to withdraw it anytime in the manner indicated in the
            Service or by contacting us at hello@plantico.io.
          </ListItem>
          <ListItem>
            <strong>Use of Personal Data on Behalf of Our Sellers</strong> - Our usage of personal
            data is in compliance with the instructions provided by the data controller, our
            vendors, and, as necessary, for the fulfillment of the contract that governs our
            provision of services or to execute actions that you request before subscribing to our
            services.
          </ListItem>
        </UnorderedList>
        <Heading as="h3" size="md">
          Use for new purposes
        </Heading>
        <Text>
          As a data controller, we may use your personal information for purposes not mentioned in
          this Privacy Policy if it is allowed by law and is consistent with the original purpose
          for which it was collected. If we require your personal information for an unrelated
          reason, we will inform you and clarify the relevant legal basis.
        </Text>
        <Heading as="h3" size="md">
          Retention
        </Heading>
        <Text>
          As part of our commitment to protecting your privacy, we will only keep your personal
          information for as long as it is necessary to fulfill the reasons for which we collected
          it. This includes meeting legal, accounting, or reporting requirements. We take into
          account various factors, such as the type, amount, and sensitivity of the personal
          information, the potential risk of unauthorized use or disclosure, and whether we can
          achieve our purposes through alternative means. Once we no longer need the personal
          information, we will remove it from your content. In certain situations, we may
          de-identify your personal information (so that it can no longer be connected to you). If
          this happens, we may use the information indefinitely without informing you.
        </Text>
        <Heading as="h4" size="sm">
          Your rights
        </Heading>
        <Text>
          As a data controller, we are obliged to comply with the European data protection laws that
          provide certain rights to European Union users with regards to their personal information.
          If you are within the European Union, you have the right to request us to take specific
          actions in relation to your personal information that we hold.
        </Text>
        <UnorderedList>
          <ListItem>
            Opt-out. You can choose to stop receiving direct marketing communications from us.
            However, you will still receive non-marketing emails related to our services.
          </ListItem>
          <ListItem>
            Access. You have the right to access your personal information and obtain information
            about how we process it.
          </ListItem>
          <ListItem>
            Correct. You can request to update or correct any inaccuracies in your personal
            information.
          </ListItem>
          <ListItem>Delete. You can request to have your personal information deleted.</ListItem>
          <ListItem>
            Transfer. You can request a machine-readable copy of your personal information to be
            transferred to you or a third party of your choice.
          </ListItem>
          <ListItem>
            Restrict. You have the option to restrict the processing of your personal information.
          </ListItem>
          <ListItem>
            Object. You can object to our reliance on our legitimate interests as the basis of our
            processing of your personal information.
          </ListItem>
        </UnorderedList>
        <Text>
          As a means of submitting requests, you have the option to send an email to
          hello@plantico.io or utilize our postal address. To ensure proper processing of your
          request and confirm your identity, we may require specific information from you. It is
          possible that applicable laws may prevent us from accommodating your request. If we are
          unable to fulfill your request, we will provide you with an explanation, subject to legal
          constraints. In the event that you wish to register a complaint regarding our use of your
          personal information or our response to your requests, you can contact us using the
          aforementioned methods or submit a complaint to the data protection regulator in your
          jurisdiction. You can locate your data protection regulator at this location.
        </Text>
        <Heading as="h3" size="md">
          Cross-Border Data Transfer
        </Heading>
        <Text>
          Under circumstances where we transfer your personal data outside the EEA to countries that
          lack adequate personal data protection, we will base the transfer on any of the following:
        </Text>
        <UnorderedList>
          <ListItem>
            Compliance with standard contractual clauses, EU-US Privacy Shield, or Binding Corporate
            Rules by the recipient
          </ListItem>
          <ListItem>Consent of the individual to whom the personal information pertains</ListItem>
          <ListItem>Any other applicable EEA requirements that allow such transfer.</ListItem>
        </UnorderedList>
        <Text>
          In case you require more details regarding the specific mechanism used by us when
          transferring your personal information out of the EEA, kindly contact us.
        </Text>
      </VStack>
    </Container>
  </PageWrapper>
)

export default Privacy
